import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, EMPTY, firstValueFrom, switchMap } from 'rxjs';

import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { GlobalStore } from '@shared/store/global/global.store';
import { InvoiceModel } from '@shared/store/invoice/invoice.model';
import { InvoiceService } from '@shared/store/invoice/invoice.service';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { PurchaseOrdersService } from '@shared/store/purchase-orders/purchase-orders.service';
import { WorkflowService } from '@shared/store/workflow/workflow.service';

import { InvoiceDetailGoBackComponent } from './invoice-detail-go-back.component';
import { InvoiceDetailStatusBannerComponent } from './invoice-detail-status-banner.component';
import { InvoiceDetailTitleComponent } from './invoice-detail-title.component';
import { InvoiceFormComponent } from './invoice-form.component';
import { InvoiceTabsComponent } from './invoice-tabs/invoice-tabs.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InvoiceDetailGoBackComponent,
    InvoiceDetailTitleComponent,
    InvoiceDetailStatusBannerComponent,
    InvoiceFormComponent,
    InvoiceTabsComponent,
  ],
  template: `
    @if (loading() || trialAttributesLoading()) {
      <div class="spinner m-auto mt-40 h-32 w-32 border-8"></div>
    } @else if (!invoice()) {
      <div class="m-auto mt-40 text-center">No invoice found</div>
    } @else {
      <div class="pt-5"></div>
      <aux-invoice-detail-go-back />
      <aux-invoice-detail-title [invoice]="invoice()!" />
      <aux-invoice-detail-status-banner
        [invoiceFormComponent]="invoiceForm()!"
        [invoice]="invoice()!"
      />

      @if (authQuery.adminUser() || !isInQueue()) {
        <aux-invoice-form [invoice]="invoice()!" />
        <aux-invoice-tabs [invoice]="invoice()!" />
      }
    }
  `,
})
export class InvoiceDetailComponent {
  authQuery = inject(AuthQuery);
  globalStore = inject(GlobalStore);

  private invoiceService = inject(InvoiceService);
  private organizationService = inject(OrganizationService);
  private overlayService = inject(OverlayService);
  private purchaseOrdersService = inject(PurchaseOrdersService);
  private route = inject(ActivatedRoute);
  private workflowService = inject(WorkflowService);

  invoice = signal<InvoiceModel | null>(null);
  loading = signal(false);

  isInQueue = computed(() => this.invoice()?.invoice_status === 'STATUS_IN_QUEUE');

  invoiceForm = viewChild(InvoiceFormComponent);
  invoiceTabs = viewChild(InvoiceTabsComponent);

  trialAttributesLoading = this.globalStore.getLoading('TrialAttributes');

  async canDeactivate() {
    const cmp = this.invoiceForm();
    const cmpTabs = this.invoiceTabs();

    if (!cmp) {
      return true;
    }

    const bool = cmp.canDeactivate();
    const boolTabs = cmpTabs ? cmpTabs.canDeactivate() : true;

    if (!bool || !boolTabs) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  constructor() {
    this.workflowService.getWorkflowList().pipe(takeUntilDestroyed()).subscribe();

    this.route.paramMap
      .pipe(
        switchMap((params) => {
          this.invoice.set(null);
          this.loading.set(true);

          let id = params.get('id') || '';

          // check if id has ? or % in it
          if (id.includes('?')) {
            id = id.substring(0, id.indexOf('?'));
          } else if (id.includes('%')) {
            id = id.substring(0, id.indexOf('%'));
          }

          if (!id) {
            this.loading.set(false);

            return EMPTY;
          }

          return combineLatest([
            this.invoiceService.getOne(id),
            this.organizationService.get(),
            this.purchaseOrdersService.get(),
          ]);
        }),
        takeUntilDestroyed()
      )
      .subscribe(([invoice]) => {
        this.invoice.set(invoice || null);
        this.loading.set(false);
      });
  }
}
